// src/i18n/index.js
import { ref } from "vue";
import ar from "./ar";
import en from "./en";

export const messages = { ar, en };

const STORAGE_KEY = "app_lang";

export function normalizeLang(lang) {
  return String(lang || "").toLowerCase().startsWith("en") ? "en" : "ar";
}

export function getSavedLang() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return normalizeLang(saved);
  } catch {}
  if (typeof document !== "undefined") {
    return normalizeLang(document.documentElement.lang || "ar");
  }
  return "ar";
}

// ✅ Centralized reactive language state (Single Source of Truth)
export const currentLang = ref(getSavedLang());

export function applyHtmlLang(lang) {
  const finalLang = normalizeLang(lang);
  const dir = finalLang === "ar" ? "rtl" : "ltr";

  if (typeof document !== "undefined") {
    document.documentElement.lang = finalLang;
    document.documentElement.dir = dir;
  }

  return finalLang;
}

export function setAppLang(lang) {
  const finalLang = normalizeLang(lang);
  applyHtmlLang(finalLang);

  try {
    localStorage.setItem(STORAGE_KEY, finalLang);
  } catch {}

  if (currentLang.value !== finalLang) {
    currentLang.value = finalLang;
  }

  // ✅ Dispatch both custom events for complete backward/forward compatibility
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("app:lang", { detail: finalLang }));
    window.dispatchEvent(new CustomEvent("app:lang-changed", { detail: finalLang }));
  }

  return finalLang;
}

// ✅ Cross-tab synchronization via storage event
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY && e.newValue) {
      const next = normalizeLang(e.newValue);
      if (next !== currentLang.value) {
        applyHtmlLang(next);
        currentLang.value = next;
        window.dispatchEvent(new CustomEvent("app:lang", { detail: next }));
        window.dispatchEvent(new CustomEvent("app:lang-changed", { detail: next }));
      }
    }
  });

  window.addEventListener(
    "app:lang",
    (e) => {
      const next = normalizeLang(e?.detail);
      if (next && next !== currentLang.value) {
        currentLang.value = next;
      }
    },
    { passive: true }
  );
}

/**
 * Universal translation helper supporting flat & nested keys and variable replacement
 */
export function t(key, vars = null) {
  const lang = currentLang.value;
  const base = messages[lang];
  if (!base) return key;

  let val = base[key];
  if (val == null && key && typeof key === "string" && key.includes(".")) {
    val = key.split(".").reduce((acc, part) => acc?.[part], base);
  }
  if (val == null) {
    const fallbackBase = messages[lang === "ar" ? "en" : "ar"];
    val = fallbackBase?.[key];
  }
  if (val == null) return key;

  if (vars && typeof val === "string") {
    return val.replace(/\{(\w+)\}/g, (_, k) => (vars?.[k] != null ? vars[k] : `{${k}}`));
  }
  return val;
}
